import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { orderConflictRetryPolicy } from '../../../service/api/conflictRetry';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import type { OrderSummaryAdapters } from '../../../module/order/orderAdapters';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { StripJsonLd } from '@vs-shop/schemas/transform/withoutJsonLd';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/orders/{tokenValue}/complete';
const HTTP_METHOD = 'patch';

export type MakeCompleteOrderDeps<OShowType> = {
  readonly orderShow: Schema.Codec<OShowType, unknown>;
  readonly orderSummaryAdapters: OrderSummaryAdapters<StripJsonLd<OShowType>>;
};

export const makeCompleteOrder = <OShowType>(deps: MakeCompleteOrderDeps<OShowType>) => {
  const CompleteOrderError = createBackendApiError({
    domain: 'Order',
    endpoint: 'CompleteOrder',
    path: ENDPOINT,
    identifierField: 'orderToken',
  } as const);

  const patchOrderCompleteFetch = (tokenValue: string, note: string, surveyOptIn: boolean) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json', 'application/merge-patch+json')
        .fetch({
          params: { tokenValue },
          body: {
            notes: note,
            newsletterOptIn: true,
            surveyOptIn,
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'order.token': tokenValue,
        },
      })
    );

  const completeOrder = (tokenValue: string, note: string, surveyOptIn: boolean) =>
    patchOrderCompleteFetch(tokenValue, note, surveyOptIn).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.orderShow)(response),
          404: (response) =>
            Effect.fail(new CompleteOrderError.NotFoundError({ orderToken: tokenValue, response })),
          422: CompleteOrderError.createViolationError(tokenValue),
          orElse: (response) =>
            Effect.fail(
              new CompleteOrderError.ApiRequestError({ orderToken: tokenValue, response })
            ),
        })
      ),
      Effect.retry(orderConflictRetryPolicy),
      Effect.tap((order) =>
        logApiOperation(
          ApiModule.OrderComplete,
          'Debug',
          `Successfully completed order ${tokenValue} -> number: ${deps.orderSummaryAdapters.number(order) ?? 'unknown'}`
        )
      ),
      Effect.withSpan('api.order.complete', {
        kind: 'internal',
        attributes: {
          'order.token': tokenValue,
        },
      })
    );

  return { completeOrder, CompleteOrderError };
};
